import { dispatchNotification } from '~/lib/notificationDispatch';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';
import { generatePublicAccessToken } from '~/lib/publicAccessTokens';
import { buildClientPortalPath, buildPublicContractLink } from '~/lib/tenantLinks';
import { contractDeliveryNeedsEmail, type ContractDeliveryMethod } from '~/lib/contractDeliveryOptions';
import type { ProjectContract } from '~/types/models';

type EnsureContractPublicAccessInput = {
    contract: ProjectContract;
    origin: string;
    tenantId: string;
    clientId?: string;
    projectId?: string;
};

type DeliverContractReadyInput = {
    tenantId: string;
    contract: ProjectContract;
    publicLink: string;
    deliveryMethod?: ContractDeliveryMethod;
    recipientName?: string;
    recipientEmail?: string;
    clientId?: string;
    clientSlug?: string;
    projectId?: string;
    projectName?: string;
    clientName?: string;
    actorId?: string;
    actorName?: string;
    actorAvatarURL?: string;
};

function normalizeText(value: unknown): string {
    return String(value || '').trim();
}

export function ensureContractPublicAccess(input: EnsureContractPublicAccessInput): { contract: ProjectContract; publicLink: string } {
    const scope = input.contract.scope || (input.projectId ? 'project' : 'client');
    const token = normalizeText(input.contract.publicAccessToken) || generatePublicAccessToken();
    const publicSharedAt = new Date();
    const contract = {
        ...input.contract,
        scope,
        clientId: normalizeText(input.contract.clientId) || normalizeText(input.clientId) || undefined,
        projectId: scope === 'project'
            ? (normalizeText(input.contract.projectId) || normalizeText(input.projectId) || undefined)
            : undefined,
        publicAccessToken: token,
        publicSharedAt,
    } satisfies ProjectContract;

    const publicLink = buildPublicContractLink({
        origin: input.origin,
        token,
        tenantId: input.tenantId,
        clientId: scope === 'client' ? contract.clientId : undefined,
        projectId: scope === 'project' ? contract.projectId : undefined,
    });

    return { contract, publicLink };
}

export async function deliverContractReady(input: DeliverContractReadyInput): Promise<void> {
    const deliveryMethod = input.deliveryMethod || 'both';
    if (contractDeliveryNeedsEmail(deliveryMethod) && !normalizeText(input.recipientEmail)) {
        throw new Error('Add an email address to the client contact before emailing this contract.');
    }
    if (deliveryMethod !== 'email' && (!input.clientId || !input.clientSlug)) {
        throw new Error('Client portal context is required to share this contract.');
    }
    // Email delivery remains independent of portal notification delivery.
    if (contractDeliveryNeedsEmail(deliveryMethod)) {
        await $fetch('/api/send-email', {
            method: 'POST',
            headers: await getRequiredAuthHeaders(),
            body: {
                to: input.recipientEmail, tenantId: input.tenantId, template: 'contract_ready',
                actionLink: input.publicLink, actionLabel: 'Open contract',
                clientName: input.clientName, recipientName: input.recipientName,
                projectName: input.projectName, documentTitle: input.contract.title,
            },
        });
    }
    const title = normalizeText(input.contract.title)
        ? `Contract ready: ${input.contract.title}`
        : input.projectId
            ? 'Project contract ready to sign'
            : 'Client agreement ready to sign';
    const body = input.projectId
        ? 'A contract is ready for review and signature.'
        : 'A client agreement is ready for review and signature.';

    if (deliveryMethod !== 'email' && input.clientId && input.clientSlug) {
        await dispatchNotification({
            tenantId: input.tenantId,
            surface: 'client',
            eventType: 'contract_ready',
            title,
            body,
            recipients: [{
                type: 'portal_access',
                clientId: input.clientId,
                ...(input.projectId ? { projectId: input.projectId } : {}),
                ...(input.projectId ? { permission: 'viewProjects' as const } : {}),
            }],
            severity: 'critical',
            clientId: input.clientId,
            projectId: input.projectId,
            entityType: 'contract',
            entityId: input.projectId
                ? `${input.projectId}:contract:v${input.contract.version}`
                : `${input.clientId}:contract:v${input.contract.version}`,
            actionPath: buildClientPortalPath({
                tenantId: input.tenantId,
                clientSlug: input.clientSlug,
                ...(input.projectId
                    ? { section: 'projects' as const, resourceId: input.projectId }
                    : {}),
            }),
            actionLabel: 'Review contract',
            dedupeKey: input.projectId
                ? `contract_ready:${input.projectId}:v${input.contract.version}`
                : `contract_ready:client:${input.clientId}:v${input.contract.version}`,
            dedupeWindowHours: 24,
            actorId: input.actorId,
            actorName: input.actorName,
            actorAvatarURL: input.actorAvatarURL,
        });
    }

}
